import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, Grid, Button, Slider, Typography, Paper } from "@mui/material";
import { motion } from "framer-motion";

const soundFiles = {
  drums: [
    "/sounds/drum1.mp3",
    "/sounds/drum2.mp3",
    "/sounds/drum3.mp3",
    "/sounds/drum4.mp3",
    "/sounds/drum5.mp3",
  ],
  kicks: [
    "/sounds/kick1.mp3",
    "/sounds/kick2.mp3",
    "/sounds/kick3.mp3",
    "/sounds/kick4.mp3",
    "/sounds/kick5.mp3",
  ],
  claps: [
    "/sounds/clap1.mp3",
    "/sounds/clap2.mp3",
    "/sounds/clap3.mp3",
    "/sounds/clap4.mp3",
    "/sounds/clap5.mp3",
  ],
  synths: [
    "/sounds/synth1.mp3",
    "/sounds/synth2.mp3",
    "/sounds/synth3.mp3",
    "/sounds/synth4.mp3",
    "/sounds/synth5.mp3",
  ],
  bass: [
    "/sounds/bass1.mp3",
    "/sounds/bass2.mp3",
    "/sounds/bass3.mp3",
    "/sounds/bass4.mp3",
    "/sounds/bass5.mp3",
  ],
};

const rowColors = {
  drums: "#00ffff",
  kicks: "#ff007f",
  claps: "#ffae00",
  synths: "#00ff73",
  bass: "#8a2be2",
};

export default function MusicMixer() {
  const [pitch, setPitch] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(false);
  const [activePads, setActivePads] = useState({});

  const audioRefs = useRef({});
  const pressedKeysRef = useRef({});

  // keyboard mapping
  const keyMap = useMemo(
    () => ({
      1: { row: "drums", index: 0 },
      2: { row: "drums", index: 1 },
      3: { row: "drums", index: 2 },
      4: { row: "drums", index: 3 },
      5: { row: "drums", index: 4 },
      q: { row: "kicks", index: 0 },
      w: { row: "kicks", index: 1 },
      e: { row: "kicks", index: 2 },
      r: { row: "kicks", index: 3 },
      t: { row: "kicks", index: 4 },
      a: { row: "claps", index: 0 },
      s: { row: "claps", index: 1 },
      d: { row: "claps", index: 2 },
      f: { row: "claps", index: 3 },
      g: { row: "claps", index: 4 },
      z: { row: "synths", index: 0 },
      x: { row: "synths", index: 1 },
      c: { row: "synths", index: 2 },
      v: { row: "synths", index: 3 },
      b: { row: "synths", index: 4 },
      y: { row: "bass", index: 0 },
      u: { row: "bass", index: 1 },
      i: { row: "bass", index: 2 },
      o: { row: "bass", index: 3 },
      p: { row: "bass", index: 4 },
    }),
    []
  );

  // reverse lookup for badges
  const padLabels = useMemo(() => {
    const map = {};
    Object.entries(keyMap).forEach(([k, v]) => {
      const padKey = `${v.row}-${v.index}`;
      const label = k.toUpperCase();
      if (map[padKey]) map[padKey] = `${map[padKey]},${label}`;
      else map[padKey] = label;
    });
    return map;
  }, [keyMap]);

  // helpers
  const setPadActive = (padKey, active) => {
    setActivePads((prev) => {
      const copy = { ...prev };
      if (active) copy[padKey] = true;
      else delete copy[padKey];
      return copy;
    });
  };

  const preloadAllAudio = () => {
    Object.keys(soundFiles).forEach((row) => {
      soundFiles[row].forEach((url, i) => {
        const key = `${row}-${i}`;
        if (!audioRefs.current[key]) {
          const a = new Audio(url);
          try {
            a.crossOrigin = "anonymous";
          } catch (e) {}
          a.preload = "auto";
          audioRefs.current[key] = a;
        }
      });
    });
  };

  const handlePlay = (row, index) => {
    const key = `${row}-${index}`;
    if (!audioRefs.current[key]) {
      const a = new Audio(soundFiles[row][index]);
      try {
        a.crossOrigin = "anonymous";
      } catch (e) {}
      a.addEventListener("ended", () => {
        if (!a.loop) setPadActive(key, false);
        a._isPlaying = false;
      });
      a.addEventListener("pause", () => {
        setPadActive(key, false);
        a._isPlaying = false;
      });
      audioRefs.current[key] = a;
    }

    const audio = audioRefs.current[key];
    audio.playbackRate = speed;
    audio.loop = loop;
    audio.currentTime = 0;
    audio._pauseAfterPlay = false;

    // play and coordinate promise
    const p = audio.play();
    audio._playPromise = p;
    if (p && typeof p.then === "function") {
      p.then(() => {
        audio._playPromise = null;
        audio._isPlaying = true;
        setPadActive(key, true);
        if (audio._pauseAfterPlay) {
          try {
            audio.pause();
            audio.currentTime = 0;
          } catch (e) {}
          audio._isPlaying = false;
          audio._pauseAfterPlay = false;
          setPadActive(key, false);
        }
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Audio play failed:", err, soundFiles[row][index]);
        audio._playPromise = null;
      });
    }
  };

  const handleStop = (row, index) => {
    const key = `${row}-${index}`;
    const audio = audioRefs.current[key];
    if (!audio) return;
    // if already playing
    if (audio._isPlaying) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {}
      audio._isPlaying = false;
      audio._pauseAfterPlay = false;
      setPadActive(key, false);
      return;
    }
    // if play pending
    if (audio._playPromise) {
      audio._pauseAfterPlay = true;
      return;
    }
    // fallback
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch (e) {}
    audio._isPlaying = false;
    audio._pauseAfterPlay = false;
    setPadActive(key, false);
  };

  const handlePauseAll = () => {
    Object.values(audioRefs.current).forEach((audio) => {
      try {
        audio.pause();
      } catch (e) {}
    });
    setActivePads({});
  };

  const handleStopAll = () => {
    Object.values(audioRefs.current).forEach((audio) => {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {}
    });
    setActivePads({});
  };

  const handlePlayAll = () => {
    Object.keys(soundFiles).forEach((row) => handlePlay(row, 0));
  };

  // Toggle helper: if playing -> stop, otherwise play
  const handleToggle = (row, index) => {
    const key = `${row}-${index}`;
    preloadAllAudio();
    const audio = audioRefs.current[key];
    if (audio && audio._isPlaying) {
      handleStop(row, index);
      setPadActive(key, false);
    } else {
      handlePlay(row, index);
      setPadActive(key, true);
    }
  };

  // keyboard handlers — toggle on keydown (press once to start, press again to stop)
  useEffect(() => {
    const handleKeyDown = (ev) => {
      const k = ev.key.toLowerCase();
      const mapping = keyMap[k];
      if (!mapping) return;
      // Ignore auto-repeat until key is released
      if (pressedKeysRef.current[k]) return;
      pressedKeysRef.current[k] = true;
      // ensure audio objects exist
      preloadAllAudio();
      const padKey = `${mapping.row}-${mapping.index}`;
      const audio = audioRefs.current[padKey];
      // if audio exists and is playing -> stop it, otherwise play it
      if (audio && audio._isPlaying) {
        handleStop(mapping.row, mapping.index);
        setPadActive(padKey, false);
      } else {
        handlePlay(mapping.row, mapping.index);
        setPadActive(padKey, true);
      }
      ev.preventDefault && ev.preventDefault();
    };

    const handleKeyUp = (ev) => {
      const k = ev.key.toLowerCase();
      // clear pressed flag; don't stop here because we toggle on keydown
      if (pressedKeysRef.current[k]) pressedKeysRef.current[k] = false;
      ev.preventDefault && ev.preventDefault();
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [keyMap]);

  // layout
  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
        background: "#0d1117",
        borderRadius: 3,
        p: 3,
        color: "#fff",
        minHeight: "80vh",
        boxShadow: "0 0 30px rgba(0,0,0,0.5)",
      }}
    >
      <Grid container spacing={2} sx={{ width: "70%" }}>
        {Object.keys(soundFiles).map((row) => {
          const color = rowColors[row];
          return (
            <Grid
              container
              item
              key={row}
              sx={{ display: "flex", gap: 2, width: "100%" }}
            >
              {soundFiles[row].map((sound, cIndex) => {
                const padKey = `${row}-${cIndex}`;
                const isActive = !!activePads[padKey];
                return (
                  <Grid
                    item
                    key={cIndex}
                    sx={{ flex: "1 1 0", display: "flex", minWidth: 0 }}
                  >
                    <motion.div
                      whileTap={{ scale: 0.95, boxShadow: `0 0 30px ${color}` }}
                      transition={{ type: "spring", stiffness: 300 }}
                      style={{ width: "100%", display: "flex" }}
                    >
                      <Button
                        onPointerDown={() => {
                          /* prevent accidental press/drag starting audio; toggle on click */
                        }}
                        onPointerUp={() => {}}
                        onTouchStart={() => {}}
                        onTouchEnd={() => {}}
                        onClick={() => handleToggle(row, cIndex)}
                        fullWidth
                        sx={{
                          position: "relative",
                          height: { xs: 56, sm: 72, md: 80 },
                          minWidth: 0,
                          px: 1,
                          background: isActive
                            ? `linear-gradient(145deg, ${color}33, ${color}22)`
                            : "linear-gradient(145deg, #1a1a1a, #2a2a2a)",
                          color,
                          borderRadius: 2,
                          fontWeight: "bold",
                          fontSize: { xs: 11, sm: 13, md: 14 },
                          textShadow: isActive
                            ? `0 0 12px ${color}`
                            : `0 0 8px ${color}`,
                          boxShadow: isActive
                            ? `0 0 30px ${color}`
                            : `0 0 12px ${color}50`,
                          transition: "all 0.15s ease-in-out",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            background: isActive
                              ? `linear-gradient(145deg, ${color}44, ${color}33)`
                              : "linear-gradient(145deg, #2a2a2a, #3a3a3a)",
                            boxShadow: `0 0 35px ${color}`,
                          },
                        }}
                      >
                        {padLabels[padKey] && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 6,
                              left: 6,
                              background: "rgba(0,0,0,0.4)",
                              color: "#fff",
                              fontSize: 10,
                              px: 0.5,
                              py: 0.2,
                              borderRadius: 1,
                              border: "1px solid rgba(255,255,255,0.08)",
                              pointerEvents: "none",
                            }}
                          >
                            {padLabels[padKey]}
                          </Box>
                        )}
                        {row.toUpperCase()} {cIndex + 1}
                      </Button>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          );
        })}
      </Grid>

      <Paper
        sx={{
          width: "30%",
          background: "#161b22",
          p: 3,
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxShadow: "inset 0 0 15px #00ffff60",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ textAlign: "center", color: "#00ffff", mb: 5 }}
          >
            🎚 Controls
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              onClick={handlePlayAll}
              sx={{ mt: 1, borderColor: "#00ffff", color: "#00ffff" }}
            >
              ▶ Play All
            </Button>
            <Button
              variant="outlined"
              onClick={handlePauseAll}
              sx={{ mt: 1, borderColor: "#00ffff", color: "#00ffff" }}
            >
              ⏸ Pause All
            </Button>
            <Button
              variant="outlined"
              onClick={handleStopAll}
              sx={{ mt: 1, borderColor: "#00ffff", color: "#00ffff" }}
            >
              ⏹ Stop All
            </Button>
            <Button
              variant={loop ? "contained" : "outlined"}
              onClick={() => setLoop(!loop)}
              sx={{ mt: 1, borderColor: "#00ffff", color: "#00ffff" }}
            >
              {loop ? "Loop: On" : "Loop: Off"}
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ mt: 2, textAlign: "center", color: "#aaa" }}
          >
            Press button or key to play. Pres again to stop.
          </Typography>
        </Box>

        <Typography
          variant="caption"
          sx={{ mt: 4, color: "#888", textAlign: "center" }}
        >
          uBLogic MusicMixer — powered by React 🎵
        </Typography>
      </Paper>
    </Box>
  );
}
